import { useCallback } from 'react'
import { ApiError, submitQuery, toApiError } from '@/lib/api'
import { useSessionStore } from '@/stores/sessionStore'
import { useHistoryStore } from '@/stores/historyStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useApiContext } from './useApiContext'

/**
 * The in-flight request is tracked at module scope rather than per hook
 * instance, for two reasons:
 *
 *  1. Only one query may be in flight at a time. The backend keeps
 *     conversation state per `conversation_id`, so two concurrent queries on
 *     one conversation would interleave server-side context.
 *  2. The thread lives in a global store, so a query must survive navigating
 *     away from the chat view — and must still be cancellable from wherever
 *     the user ends up.
 */
let activeController: AbortController | null = null
let activeExchangeId: string | null = null

export interface RunQueryApi {
  run: (query: string) => Promise<void>
  cancel: () => void
  isRunning: boolean
}

/**
 * The single write path for running a query. Views call `run` and render from
 * the session store; no view issues a fetch of its own.
 */
export function useRunQuery(): RunQueryApi {
  const context = useApiContext()
  const route = useSettingsStore((state) => state.route)
  const database = useSettingsStore((state) => state.database)
  const rememberHistory = useSettingsStore((state) => state.rememberHistory)

  const startExchange = useSessionStore((state) => state.startExchange)
  const resolveExchange = useSessionStore((state) => state.resolveExchange)
  const failExchange = useSessionStore((state) => state.failExchange)
  const cancelExchange = useSessionStore((state) => state.cancelExchange)
  const recordHistory = useHistoryStore((state) => state.record)

  const isRunning = useSessionStore((state) =>
    state.exchanges.some((exchange) => exchange.status === 'pending'),
  )

  const cancel = useCallback(() => {
    if (activeExchangeId) cancelExchange(activeExchangeId)
    activeController?.abort()
    activeController = null
    activeExchangeId = null
  }, [cancelExchange])

  const run = useCallback(
    async (rawQuery: string) => {
      const query = rawQuery.trim()
      if (!query) return

      // Supersede anything still in flight. Without this, a fast follow-up can
      // resolve before the request it replaced and render out of order.
      if (activeController) cancel()

      // Read the conversation id at submit time: starting a new session
      // mid-flight must not attribute this response to the fresh conversation.
      const conversationId = useSessionStore.getState().conversationId

      const controller = new AbortController()
      const exchangeId = startExchange({ query, route })

      activeController = controller
      activeExchangeId = exchangeId

      const startedAt = performance.now()

      try {
        const outcome = await submitQuery(context, {
          query,
          conversationId,
          route,
          database,
          signal: controller.signal,
        })

        const durationMs = performance.now() - startedAt
        resolveExchange(exchangeId, outcome, durationMs)

        if (rememberHistory) {
          recordHistory({
            query,
            conversationId,
            route,
            status: 'success',
            rowCount: outcome.result.rowCount,
            durationMs,
          })
        }
      } catch (error) {
        const apiError =
          error instanceof ApiError ? error : toApiError(error, 'The query failed.')

        // A superseded or user-cancelled request is not a failure to report.
        if (apiError.kind === 'aborted') return

        const durationMs = performance.now() - startedAt

        failExchange(
          exchangeId,
          {
            message: apiError.message,
            kind: apiError.kind,
            ...(apiError.code ? { code: apiError.code } : {}),
            ...(apiError.status ? { status: apiError.status } : {}),
          },
          durationMs,
        )

        if (rememberHistory) {
          recordHistory({
            query,
            conversationId,
            route,
            status: 'error',
            durationMs,
            errorMessage: apiError.message,
          })
        }
      } finally {
        if (activeController === controller) {
          activeController = null
          activeExchangeId = null
        }
      }
    },
    [
      cancel,
      context,
      database,
      failExchange,
      recordHistory,
      rememberHistory,
      resolveExchange,
      route,
      startExchange,
    ],
  )

  return { run, cancel, isRunning }
}
